import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Grid,
  Collapse,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Clear as ClearIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { PropertySearchFilters, PropertyType, ListingType } from '@/types';

interface PropertyFiltersProps {
  filters: PropertySearchFilters;
  onFilterChange: (filters: PropertySearchFilters) => void;
  onClose?: () => void;
}

const FEATURE_OPTIONS = [
  { label: 'Pool', value: 'pool' },
  { label: 'Garage', value: 'garage' },
  { label: 'Garden', value: 'garden' },
  { label: 'Balcony', value: 'balcony' },
  { label: 'Fireplace', value: 'fireplace' },
  { label: 'Smart Home', value: 'smartHome' },
  { label: 'Energy Efficient', value: 'energyEfficient' },
  { label: 'Basement', value: 'basement' },
  { label: 'Attic', value: 'attic' },
  { label: 'Pet Friendly', value: 'petFriendly' },
  { label: 'Furnished', value: 'furnished' },
];

const AMENITY_OPTIONS = [
  'Air Conditioning',
  'Heating',
  'Washer/Dryer',
  'Elevator',
  'Wheelchair Access',
  'Gym',
  'Parking',
  'WiFi',
  'Concierge',
  'Security System',
  'Storage',
  'Dishwasher',
];

const PropertyFilters: React.FC<PropertyFiltersProps> = ({
  filters,
  onFilterChange,
  onClose,
}) => {
  const [localFilters, setLocalFilters] = useState<PropertySearchFilters>(filters);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof PropertySearchFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleResetFilters = () => {
    const emptyFilters: PropertySearchFilters = {};
    setLocalFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const handlePriceRangeChange = (value: number | number[]) => {
    const [min, max] = value as number[];
    handleFilterChange('priceRange', { min, max });
  };

  const handleBedroomRangeChange = (value: number | number[]) => {
    const [min, max] = value as number[];
    handleFilterChange('bedrooms', { min, max });
  };

  const handleBathroomRangeChange = (value: number | number[]) => {
    const [min, max] = value as number[];
    handleFilterChange('bathrooms', { min, max });
  };

  const handleSquareFootageRangeChange = (value: number | number[]) => {
    const [min, max] = value as number[];
    handleFilterChange('squareFootage', { min, max });
  };

  const handleFeatureToggle = (feature: string) => {
    const currentFeatures = localFilters.features || [];
    const newFeatures = currentFeatures.includes(feature)
      ? currentFeatures.filter((f) => f !== feature)
      : [...currentFeatures, feature];
    handleFilterChange('features', newFeatures);
  };

  const handleAmenityToggle = (amenity: string) => {
    const currentAmenities = localFilters.amenities || [];
    const newAmenities = currentAmenities.includes(amenity)
      ? currentAmenities.filter((a) => a !== amenity)
      : [...currentAmenities, amenity];
    handleFilterChange('amenities', newAmenities);
  };

  const hasActiveFilters = () => {
    return (
      !!localFilters.searchTerm ||
      (localFilters.propertyTypes && localFilters.propertyTypes.length > 0) ||
      (localFilters.listingTypes && localFilters.listingTypes.length > 0) ||
      localFilters.priceRange ||
      localFilters.bedrooms ||
      localFilters.bathrooms ||
      localFilters.squareFootage ||
      localFilters.location?.city ||
      localFilters.location?.state ||
      localFilters.location?.zipCode ||
      localFilters.isActive !== undefined ||
      localFilters.isFeatured !== undefined ||
      (localFilters.features && localFilters.features.length > 0) ||
      (localFilters.amenities && localFilters.amenities.length > 0)
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <Box>
          {hasActiveFilters() && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleResetFilters}
              sx={{ mr: 1 }}
            >
              Clear All
            </Button>
          )}
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Search Term */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Search Properties"
            placeholder="Search by title..."
            value={localFilters.searchTerm || ''}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          />
        </Grid>

        {/* Property Types */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Property Type</InputLabel>
            <Select
              multiple
              value={localFilters.propertyTypes || []}
              onChange={(e) => handleFilterChange('propertyTypes', e.target.value)}
              input={<OutlinedInput label="Property Type" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Object.values(PropertyType).map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={(localFilters.propertyTypes || []).indexOf(type) > -1} />
                  <ListItemText primary={type.charAt(0).toUpperCase() + type.slice(1)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Listing Types */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Listing Type</InputLabel>
            <Select
              multiple
              value={localFilters.listingTypes || []}
              onChange={(e) => handleFilterChange('listingTypes', e.target.value)}
              input={<OutlinedInput label="Listing Type" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Object.values(ListingType).map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={(localFilters.listingTypes || []).indexOf(type) > -1} />
                  <ListItemText primary={type.charAt(0).toUpperCase() + type.slice(1)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Price Range */}
        <Grid item xs={12} md={4}>
          <Typography variant="caption" gutterBottom>Price Range</Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={[
                localFilters.priceRange?.min || 0,
                localFilters.priceRange?.max || 10000000,
              ]}
              onChange={(_, value) => handlePriceRangeChange(value)}
              valueLabelDisplay="auto"
              min={0}
              max={10000000}
              step={10000}
              valueLabelFormat={(value) => `$${value.toLocaleString()}`}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              type="number"
              label="Min Price"
              size="small"
              value={localFilters.priceRange?.min || ''}
              onChange={(e) =>
                handleFilterChange('priceRange', {
                  min: parseInt(e.target.value) || 0,
                  max: localFilters.priceRange?.max || 10000000,
                })
              }
            />
            <TextField
              type="number"
              label="Max Price"
              size="small"
              value={localFilters.priceRange?.max || ''}
              onChange={(e) =>
                handleFilterChange('priceRange', {
                  min: localFilters.priceRange?.min || 0,
                  max: parseInt(e.target.value) || 10000000,
                })
              }
            />
          </Box>
        </Grid>

        {/* Location - Grouped */}
        <Grid item xs={12}>
           <Typography variant="subtitle2" gutterBottom>Location</Typography>
           <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="City"
                value={localFilters.location?.city || ''}
                onChange={(e) =>
                  handleFilterChange('location', {
                    ...localFilters.location,
                    city: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="State"
                value={localFilters.location?.state || ''}
                onChange={(e) =>
                  handleFilterChange('location', {
                    ...localFilters.location,
                    state: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Zip Code"
                value={localFilters.location?.zipCode || ''}
                onChange={(e) =>
                  handleFilterChange('location', {
                    ...localFilters.location,
                    zipCode: e.target.value,
                  })
                }
              />
            </Grid>
           </Grid>
        </Grid>

        {/* Basic Stats */}
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" gutterBottom>Bedrooms: {localFilters.bedrooms?.min || 0} - {localFilters.bedrooms?.max || 10}+</Typography>
          <Box sx={{ px: 2 }}>
             <Slider
              value={[
                localFilters.bedrooms?.min || 0,
                localFilters.bedrooms?.max || 10,
              ]}
              onChange={(_, value) => handleBedroomRangeChange(value)}
              min={0}
              max={10}
              step={1}
              marks
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" gutterBottom>Bathrooms: {localFilters.bathrooms?.min || 0} - {localFilters.bathrooms?.max || 10}+</Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={[
                localFilters.bathrooms?.min || 0,
                localFilters.bathrooms?.max || 10,
              ]}
              onChange={(_, value) => handleBathroomRangeChange(value)}
              min={0}
              max={10}
              step={0.5}
              marks
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" gutterBottom>Square Footage</Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={[
                localFilters.squareFootage?.min || 0,
                localFilters.squareFootage?.max || 10000,
              ]}
              onChange={(_, value) => handleSquareFootageRangeChange(value)}
              min={0}
              max={10000}
              step={100}
              valueLabelFormat={(value) => `${value.toLocaleString()} sq ft`}
              valueLabelDisplay="auto"
            />
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Button
          endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          color="inherit"
        >
          {showAdvanced ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
        </Button>
      </Box>

      <Collapse in={showAdvanced}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Features & Amenities</Typography>
          <FormGroup row>
            {FEATURE_OPTIONS.map((feature) => (
              <FormControlLabel
                key={feature.value}
                control={
                  <Checkbox
                    checked={(localFilters.features || []).includes(feature.value)}
                    onChange={() => handleFeatureToggle(feature.value)}
                    size="small"
                  />
                }
                label={feature.label}
                sx={{ mr: 3 }}
              />
            ))}
          </FormGroup>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Amenities</Typography>
          <FormGroup row>
            {AMENITY_OPTIONS.map((amenity) => (
              <FormControlLabel
                key={amenity}
                control={
                  <Checkbox
                    checked={(localFilters.amenities || []).includes(amenity)}
                    onChange={() => handleAmenityToggle(amenity)}
                    size="small"
                  />
                }
                label={amenity}
                sx={{ mr: 3 }}
              />
            ))}
          </FormGroup>

          <Grid container spacing={2} sx={{ mt: 2 }}>
             {/* Status Filters */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFilters.isActive === undefined ? 'all' : localFilters.isActive ? 'active' : 'inactive'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange(
                      'isActive',
                      value === 'all' ? undefined : value === 'active'
                    );
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Featured</InputLabel>
                <Select
                  value={localFilters.isFeatured === undefined ? 'all' : localFilters.isFeatured ? 'featured' : 'not-featured'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange(
                      'isFeatured',
                      value === 'all' ? undefined : value === 'featured'
                    );
                  }}
                  label="Featured"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="featured">Featured Only</MenuItem>
                  <MenuItem value="not-featured">Not Featured</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={handleResetFilters}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
      </Box>
    </Paper>
  );
};

export default PropertyFilters;
